import {
  COMPARE_VARIANT_LAYOUT,
  compareVariantOf,
} from '@hyphengroup/cms-components/cms-blocks/Compare/compareVariant';
import type { VerticalType } from '../types';
import {
  CmsBaseComponent,
  EnumDisplayType,
} from '$/(cms)/_modules/cmsService/types';

export type ComponentOfType<T extends CmsBaseComponent['__typename']> = Extract<
  CmsBaseComponent,
  { __typename: T }
>;

/**
 * A block renders unless an editor switched it off (`visible: false`); a block
 * without the flag counts as visible.
 */
export const isVisibleBlock = (
  block: { visible?: boolean | null } | null | undefined,
): boolean => block?.visible !== false;

/**
 * Flattens a page/article component list, descending into components nested
 * inside a Prefilled Block (`block.default_components`, recursively).
 *
 * A bare `components.find(...)` misses anything an editor placed inside a
 * Prefilled Block, so every by-type lookup over page components must go through
 * this flattened view (see `findComponentByType` / `filterComponentsByType`).
 */
export const flattenComponents = (
  components: CmsBaseComponent[] | undefined,
): CmsBaseComponent[] => {
  const result: CmsBaseComponent[] = [];

  for (const component of components ?? []) {
    result.push(component);
    if (component.__typename === 'ComponentPagesComponentPrefilledBlock') {
      result.push(...flattenComponents(component.block?.default_components));
    }
  }

  return result;
};

/**
 * First component of the given `__typename`, searching the flattened tree
 * (including Prefilled Block children). Return type is narrowed to the matching
 * union member.
 */
export const findComponentByType = <T extends CmsBaseComponent['__typename']>(
  components: CmsBaseComponent[] | undefined,
  typename: T,
): ComponentOfType<T> | undefined =>
  flattenComponents(components).find(
    (c): c is ComponentOfType<T> => c.__typename === typename,
  );

/**
 * All components of the given `__typename`, searching the flattened tree
 * (including Prefilled Block children). Return type is narrowed to the matching
 * union member.
 */
export const filterComponentsByType = <
  T extends CmsBaseComponent['__typename'],
>(
  components: CmsBaseComponent[] | undefined,
  typename: T,
): ComponentOfType<T>[] =>
  flattenComponents(components).filter(
    (c): c is ComponentOfType<T> => c.__typename === typename,
  );

/**
 * The page's M3b (PlanTierSelector) — the TI tier source for the SSR search
 * pricing and for the blocks that read M3b's tiers (M4, M6). Read regardless of
 * its `visible` flag (CMS-1724 Decision A: hiding the selector must not change
 * pricing or tiers) and from inside Prefilled Blocks.
 */
export const findPlanTierSelector = (
  components: CmsBaseComponent[] | undefined,
) =>
  findComponentByType(
    components,
    'ComponentPagesComponentPlanTierSelectorComponent',
  );

/**
 * Whether the page renders product cards that may be compared, which is where the compare control
 * sits: a card listing that has not switched comparison off (an editor's switch per listing), or
 * selected products shown as a listing (a comparison-table display of selected products draws no
 * cards). Prefilled Block children count too.
 */
export const rendersComparableCards = (
  components: CmsBaseComponent[] | undefined,
): boolean =>
  flattenComponents(components).some(
    (component) =>
      ((component.__typename === 'ComponentPagesComponentCardListingPosition' ||
        component.__typename === 'ComponentPagesComponentCcCardListing') &&
        component.comparison_enabled !== false) ||
      (component.__typename === 'ComponentPagesComponentSelectedProducts' &&
        component.display_type === EnumDisplayType.ProductListing),
  );

/**
 * Whether a page offers the side-by-side comparison, i.e. whether its vertical's comparison config
 * is worth loading: its vertical compares at all, it renders product cards, and it meets the
 * variant's own prerequisite.
 *
 * A variant that prices the trip the reader searched needs the travel search on the page. A page
 * without one would price nothing, and its provider would overwrite the selection the results page
 * remembered (a different trip scope). Any other variant compares what the cards show.
 */
export const offersComparison = (
  section: VerticalType | undefined,
  components: CmsBaseComponent[] | undefined,
): section is VerticalType => {
  const variant = compareVariantOf(section);
  if (!variant || !rendersComparableCards(components)) return false;
  if (COMPARE_VARIANT_LAYOUT[variant].pricesTrip)
    return !!findComponentByType(components, 'TravelPolicySearchComponent');
  return true;
};
